import { useEffect, useState } from "react";
import {
  FileText, Image as ImageIcon, FileSpreadsheet, FileArchive,
  FileVideo, FileAudio, File, X, Check, AlertCircle,
} from "lucide-react";

const BLUE = "#2B579A";
const BLUE_SOFT = "#4A8CD8";
const GREEN = "#22C55E";
const RED = "#EF4444";

/**
 * UploadFileCard — one row representing a file being (or having been) uploaded.
 *
 * Visual states driven by `status`:
 *   • pending    → grey border, "En attente", no bar, remove button
 *   • uploading  → blue border, percentage on the right, gradient progress bar,
 *                  "xxx Ko / y.y Mo", cancel (✕) button
 *   • done       → green border, ✓ icon + "Terminé" text, no bar, size only
 *   • error      → red border, error message, remove button
 *   • canceled   → grey border, "Annulé", remove button
 *
 * Icon color-coded by MIME/extension.
 */
export default function UploadFileCard({
  file,
  status = "pending",
  progress = 0,
  uploadedBytes = 0,
  errorMessage = "",
  onCancel,
  onRemove,
  testId,
}) {
  const isImage = (file?.type || "").startsWith("image/");
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    if (!isImage || !(file instanceof File)) return;
    const url = URL.createObjectURL(file);
    setThumb(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  const meta = statusMeta(status);
  const icon = fileIcon(file);
  const iconBg = statusIconBg(status);
  // Spec rev154 (2026-02-07) — vignette de confirmation VERTE pour l'état
  // "done" : le user veut que la zone bleue pointillée soit remplacée par
  // une carte de confirmation vert foncé une fois le fichier chargé. On
  // laisse les autres états (uploading/pending/error) sur leur fond
  // sombre neutre — seul le succès s'exprime en vert plein.
  const isDone = status === "done";
  const rowBg = isDone ? "#0F2A1C" : "#1A1A1A";
  const rowBorder = isDone ? "1.5px solid #22C55E" : `1px solid ${meta.border}`;
  const rowPadding = isDone ? "11px 13px" : "9px 11px";
  // Icône type de fichier prend le vert vif quand la carte est "done"
  // (au lieu du reste des couleurs par statut).
  const doneIconColor = "#22C55E";
  const doneIconBg = "rgba(34,197,94,0.18)";

  return (
    <div
      className="flex items-center gap-3 transition-colors"
      style={{
        background: rowBg,
        border: rowBorder,
        borderRadius: 10,
        padding: rowPadding,
      }}
      data-testid={testId}
    >
      {/* --- Type icon / thumbnail ------------------------------------- */}
      <div
        className="shrink-0 rounded flex items-center justify-center overflow-hidden"
        style={{
          width: isDone ? 32 : 28,
          height: isDone ? 32 : 28,
          background: isDone ? doneIconBg : iconBg,
          color: isDone ? doneIconColor : meta.iconColor,
        }}
      >
        {status === "error" ? (
          <AlertCircle size={15} />
        ) : isImage && thumb ? (
          <img src={thumb} alt="" className="h-full w-full object-cover" />
        ) : (
          // En état "done" on affiche l'icône du TYPE de fichier (spec :
          // "icône du type de fichier à gauche"), pas la coche — la
          // coche est déplacée à côté du libellé "Chargé".
          <span style={{ color: isDone ? doneIconColor : meta.iconColor }}>{icon}</span>
        )}
      </div>

      {/* --- Main body -------------------------------------------------- */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div
            className="text-[12.5px] font-medium truncate"
            title={file?.name}
            style={isDone ? { color: "#EDEDED" } : undefined}
          >
            {file?.name || "fichier"}
          </div>
          <div
            className="text-[11px] font-bold tabular-nums shrink-0 inline-flex items-center gap-1"
            style={{ color: isDone ? "#22C55E" : meta.rightColor }}
          >
            {status === "uploading" && `${Math.round(progress)}%`}
            {status === "done" && (
              <>
                <Check size={12} strokeWidth={2.8} />
                Chargé
              </>
            )}
            {status === "pending" && "En attente"}
            {status === "error" && "Erreur"}
            {status === "canceled" && "Annulé"}
          </div>
        </div>

        {status === "uploading" && (
          <div
            className="mt-1.5 h-1 rounded overflow-hidden"
            style={{ background: "rgba(255,255,255,0.06)" }}
          >
            <div
              className="h-full transition-all"
              style={{
                width: `${Math.max(2, Math.min(100, progress))}%`,
                background: `linear-gradient(90deg, ${BLUE}, ${BLUE_SOFT})`,
              }}
            />
          </div>
        )}

        <div
          className="mt-0.5 text-[10.5px] tabular-nums truncate"
          style={{
            color: isDone ? "rgba(255,255,255,0.55)" : undefined,
          }}
        >
          {status === "uploading" && file?.size
            ? `${humanSize(uploadedBytes)} / ${humanSize(file.size)}`
            : status === "error" && errorMessage
              ? errorMessage
              : humanSize(file?.size || 0)}
        </div>
      </div>

      {/* --- Right action ---------------------------------------------- */}
      <div className="shrink-0 flex items-center">
        {status === "uploading" ? (
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-red-400 transition-colors"
            title="Annuler l'envoi"
            data-testid={`${testId}-cancel`}
          >
            <X size={14} />
          </button>
        ) : (
          // Spec rev154 — en "done" (comme dans les autres statuts) on
          // affiche une croix ✕ à droite pour retirer/remplacer le
          // fichier. Le petit rond ✓ vert précédent est retiré : la
          // coche "Chargé" à côté du nom fait déjà office de confirmation.
          <button
            type="button"
            onClick={onRemove}
            className="transition-colors"
            style={{
              color: isDone ? "rgba(255,255,255,0.55)" : "#9CA3AF",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#F87171")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = isDone ? "rgba(255,255,255,0.55)" : "#9CA3AF")
            }
            title="Retirer"
            data-testid={`${testId}-remove`}
          >
            <X size={15} strokeWidth={2.2} />
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function statusMeta(status) {
  switch (status) {
    case "uploading":
      return { border: "rgba(43,87,154,0.4)", iconColor: BLUE_SOFT, rightColor: BLUE_SOFT };
    case "done":
      return { border: "rgba(34,197,94,0.3)", iconColor: GREEN, rightColor: GREEN };
    case "error":
      return { border: "rgba(239,68,68,0.4)", iconColor: RED, rightColor: RED };
    case "canceled":
      return { border: "rgba(255,255,255,0.10)", iconColor: "#9CA3AF", rightColor: "#9CA3AF" };
    case "pending":
    default:
      return { border: "rgba(255,255,255,0.10)", iconColor: BLUE_SOFT, rightColor: "#9CA3AF" };
  }
}

function statusIconBg(status) {
  switch (status) {
    case "done":     return "rgba(34,197,94,0.15)";
    case "error":    return "rgba(239,68,68,0.15)";
    case "canceled": return "rgba(255,255,255,0.06)";
    case "pending":
    case "uploading":
    default:         return "rgba(43,87,154,0.15)";
  }
}

function fileIcon(file) {
  const type = (file?.type || "").toLowerCase();
  const name = (file?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  const isSpreadsheet = /sheet|excel|csv/.test(type) || ["xls", "xlsx", "csv"].includes(ext);
  const isArchive = /zip|rar|7z|tar|gzip/.test(type) || ["zip", "rar", "7z", "tar", "gz"].includes(ext);
  const isVideo = type.startsWith("video/") || ["mp4", "mov", "avi", "mkv"].includes(ext);
  const isAudio = type.startsWith("audio/") || ["mp3", "wav", "flac", "aac"].includes(ext);
  const isPdfOrDoc = type === "application/pdf" || /word|document/.test(type)
    || ["pdf", "doc", "docx", "txt"].includes(ext);
  if (type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return <ImageIcon size={14} />;
  if (isSpreadsheet) return <FileSpreadsheet size={14} />;
  if (isArchive) return <FileArchive size={14} />;
  if (isVideo) return <FileVideo size={14} />;
  if (isAudio) return <FileAudio size={14} />;
  if (isPdfOrDoc) return <FileText size={14} />;
  return <File size={14} />;
}

function humanSize(b) {
  if (!b) return "0 o";
  if (b < 1024) return `${b} o`;
  if (b < 1e6) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / 1e6).toFixed(1)} Mo`;
}
